import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { queryKeysCompany } from './queryKeys';
import { zodPdfDlnRequestDoctypeSchema } from './validations';

const zodCompanyInvoiceSendingSettingsSchema = z.object({
  accountingReferenceToSubject: z.boolean().nullable(),
  addDlnsSinglePdf: z.boolean().nullable(),
  addDlnsToInvoice: z.boolean().nullable(),
  addInvoiceProtocolToInvoice: z.boolean().nullable(),
  dlnDoctype: zodPdfDlnRequestDoctypeSchema.prefault('docx'),
  dlnSimplifiedJsonsToInvoice: z.boolean().nullable(),
  dlnsMultiFileToInvoice: z.boolean().nullable(),
  emailReceivedTimeToSubject: z.boolean().nullable(),
  includeDlnAttachments: z.boolean().prefault(true),
  sendInvoiceExternalXml: z.boolean().nullable(),
});

export type CompanyInvoiceSendingSettings = z.infer<
  typeof zodCompanyInvoiceSendingSettingsSchema
>;

/**
 * Fetches company invoice sending settings from the API.
 *
 * @param companyId - The ID of the company to fetch.
 * @returns The company invoice sending settings.
 * @throws Error if the API request fails.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Company/operation/get_company_invoice_settings_company__id__invoice_sending_settings_get
 */
export const fetchCompanyInvoiceSendingSettings = async (companyId: UUID) => {
  try {
    return await vestigasApi
      .get(ENDPOINT.COMPANY.INVOICE_SETTINGS.GET(companyId), {
        validate: { response: zodCompanyInvoiceSendingSettingsSchema },
      })
      .json<CompanyInvoiceSendingSettings>();
  } catch (error) {
    log.error({
      message: 'Error fetching invoice sending settings for company',
      error,
    });

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};

/**
 * React Query based custom hook for getting the invoice sending settings for a company with a given companyId.
 *
 * @param companyId - The ID of the company to fetch.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Company/operation/get_company_invoice_settings_company__id__invoice_sending_settings_get
 */
export const useQueryCompanyInvoiceSendingSettings = (
  companyId: UUID,
  options?: Omit<
    UseQueryOptions<CompanyInvoiceSendingSettings>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<CompanyInvoiceSendingSettings>({
    enabled: Boolean(companyId),
    queryFn: async () => fetchCompanyInvoiceSendingSettings(companyId),
    queryKey: queryKeysCompany.getInvoiceSendingSettings(companyId),
    ...options,
  });
};
